/** Templates de e-mail (prévia no admin + espelho do backend). */

export type EmailTemplateKind = 'ativacao' | 'pagamento'

export interface EmailTemplateTexts {
  assunto: string
  titulo: string
  corpo: string
}

export const DEFAULT_EMAIL_TEXTS = {
  ativacao: {
    assunto: 'Assinatura confirmada — bem-vindo(a) ao Latera',
    titulo: 'Assinatura confirmada',
    corpo:
      'Obrigado por assinar o Latera. Seu pagamento foi confirmado e sua assinatura já está ativa.',
  },
  pagamento: {
    assunto: 'Seu pagamento foi realizado com sucesso — Latera',
    titulo: 'Pagamento confirmado',
    corpo:
      'Seu pagamento foi realizado com sucesso.\n\nObrigado pelo pagamento. Seguem os dados da cobrança:',
  },
} as const

export const EMAIL_PREVIEW_SAMPLE = {
  nome: 'Francisco Silva',
  planoNome: 'Plano Medium - Latera',
  valor: 1.99,
  dataProximoPagamento: new Date(Date.now() + 30 * 86400000).toISOString(),
  dataPagamento: new Date().toISOString(),
  metodo: 'pix' as 'pix' | 'cartao',
}

function escapeHtml(value: string) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

function formatCurrency(value: number) {
  return Number(value || 0).toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  })
}

function formatDateBr(isoOrDate: string) {
  if (!isoOrDate) return '—'
  const d = new Date(isoOrDate)
  if (Number.isNaN(d.getTime())) return isoOrDate
  return d.toLocaleDateString('pt-BR')
}

function corpoToHtml(corpo: string) {
  const parts = String(corpo || '')
    .split(/\n+/)
    .map((p) => p.trim())
    .filter(Boolean)
  if (parts.length === 0) return ''
  return parts
    .map(
      (p) =>
        `<p style="margin:0 0 16px;font-size:16px;line-height:1.5;">${escapeHtml(p)}</p>`
    )
    .join('')
}

export function buildEmailPreviewHtml(options: {
  titulo: string
  corpo: string
  nome: string
  detalhes: Array<[string, string]>
  appUrl?: string
}) {
  const appUrl = options.appUrl || 'https://app.latera.com.br'
  const rows = options.detalhes
    .map(
      ([label, value]) => `
      <div style="font-size:13px;color:#64748b;margin-bottom:4px;">${escapeHtml(label)}</div>
      <div style="font-size:16px;font-weight:700;margin-bottom:14px;">${escapeHtml(value)}</div>`
    )
    .join('')

  return `<!DOCTYPE html>
<html>
<head><meta charset="UTF-8" /><meta name="viewport" content="width=device-width, initial-scale=1.0" /></head>
<body style="margin:0;padding:0;background:#f3f4f6;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Arial,sans-serif;color:#111827;">
  <table width="100%" cellpadding="0" cellspacing="0" style="background:#f3f4f6;padding:24px 12px;">
    <tr><td align="center">
      <table width="100%" style="max-width:560px;background:#ffffff;border-radius:12px;overflow:hidden;border:1px solid #e5e7eb;">
        <tr>
          <td style="background:#1d4ed8;color:#ffffff;padding:28px 24px;text-align:center;">
            <div style="font-size:22px;font-weight:700;">Latera</div>
            <div style="margin-top:8px;font-size:16px;opacity:0.95;">${escapeHtml(options.titulo)}</div>
          </td>
        </tr>
        <tr>
          <td style="padding:28px 24px;">
            <p style="margin:0 0 16px;font-size:16px;line-height:1.5;">
              Olá, <strong>${escapeHtml(options.nome || 'Cliente')}</strong>!
            </p>
            ${corpoToHtml(options.corpo)}
            <table width="100%" cellpadding="0" cellspacing="0" style="background:#f8fafc;border-radius:8px;border:1px solid #e2e8f0;margin:20px 0;">
              <tr><td style="padding:16px 18px;">${rows}</td></tr>
            </table>
            <p style="text-align:center;margin:24px 0;">
              <a href="${escapeHtml(appUrl)}/dashboard"
                 style="display:inline-block;background:#1d4ed8;color:#ffffff;text-decoration:none;padding:12px 22px;border-radius:8px;font-weight:600;font-size:15px;">
                Acessar o Latera
              </a>
            </p>
          </td>
        </tr>
        <tr>
          <td style="padding:16px 24px 24px;text-align:center;font-size:12px;color:#9ca3af;">
            © ${new Date().getFullYear()} Latera. Este é um e-mail automático.
          </td>
        </tr>
      </table>
    </td></tr>
  </table>
</body>
</html>`
}

export function buildTemplatePreview(
  kind: EmailTemplateKind,
  texts: EmailTemplateTexts,
  sample = EMAIL_PREVIEW_SAMPLE
) {
  const metodoLabel =
    sample.metodo === 'cartao' ? 'Cartão de crédito' : 'PIX Automático'

  const detalhes: Array<[string, string]> =
    kind === 'ativacao'
      ? [
          ['Plano', sample.planoNome],
          ['Valor', formatCurrency(sample.valor)],
          ['Próximo vencimento', formatDateBr(sample.dataProximoPagamento)],
          ['Forma de pagamento', metodoLabel],
        ]
      : [
          ['Plano', sample.planoNome],
          ['Valor pago', formatCurrency(sample.valor)],
          ['Data do pagamento', formatDateBr(sample.dataPagamento)],
          ['Forma de pagamento', metodoLabel],
        ]

  return {
    assunto: texts.assunto,
    html: buildEmailPreviewHtml({
      titulo: texts.titulo,
      corpo: texts.corpo,
      nome: sample.nome,
      detalhes,
    }),
  }
}
